import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Download, Terminal, CheckCircle, Cpu, Monitor, Apple, Server,
  AlertTriangle, Smartphone, ShieldCheck, Zap, FileCode, Package, Lock,
  Loader2, Wifi, WifiOff,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AGENT_URL = "http://localhost:7979";

const ZERO_INSTALL_FILES = [
  {
    filename: "agente_assinador_v14.ps1",
    label: "Script PowerShell v14 (Agente Zero-Install)",
    desc: "Não requer Python. Usa Windows CNG/CAPI nativo. Recomendado.",
    icon: FileCode,
  },
  {
    filename: "iniciar_agente.bat",
    label: "Iniciar Agente (duplo-clique)",
    desc: "Inicializador para o script PowerShell.",
    icon: Terminal,
  },
  {
    filename: "build_agente.bat",
    label: "Construtor do .exe (PyInstaller)",
    desc: "Para gerar agente_assinador.exe distribuível (avançado).",
    icon: Package,
  },
];

const PYTHON_INSTALL_STEPS = [
  {
    title: "1. Baixar o Agente Local",
    desc: "Faça o download do script Python do agente local",
    code: null,
    action: "download",
  },
  {
    title: "2. Instalar dependências",
    code: "pip install flask flask-cors cryptography python-pkcs11",
  },
  {
    title: "3. Executar o agente",
    code: "python agente_local.py",
  },
  {
    title: "4. (Opcional) Especificar driver PKCS#11",
    code: 'python agente_local.py --lib "C:\\Windows\\System32\\eTPkcs11.dll"',
  },
];

const DRIVERS = [
  { name: "Gemalto/Thales eToken", windows: "eTPkcs11.dll", linux: "libeTPkcs11.so" },
  { name: "SafeNet eToken Pro", windows: "siecap11.dll / aetpkss1.dll", linux: "libeTPkcs11.so" },
  { name: "Bit4Id", windows: "bit4ipki.dll", linux: "libbit4ipki.so" },
  { name: "VaultID CESS Desktop", windows: "cess_pkcs11_64.dll", linux: "N/A" },
  { name: "OpenSC (genérico)", windows: "opensc-pkcs11.dll", linux: "opensc-pkcs11.so" },
];

async function downloadFile(filename, fallbackName) {
  try {
    const res = await fetch(`${BACKEND_URL}/api/download/agent/${filename}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fallbackName || filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    alert("Erro ao baixar " + filename + ": " + e.message);
  }
}

export default function AgentePage() {
  const navigate = useNavigate();

  // Health check state
  const [healthState, setHealthState] = useState("idle"); // idle | checking | online | offline
  const [healthData, setHealthData] = useState(null);

  const checkHealth = async () => {
    setHealthState("checking");
    setHealthData(null);
    try {
      const res = await fetch(`${AGENT_URL}/status`, { signal: AbortSignal.timeout(4000) });
      const data = await res.json();
      setHealthState("online");
      setHealthData(data);
    } catch (e) {
      setHealthState("offline");
      setHealthData({ error: e.message });
    }
  };

  const handleDownloadPython = () => downloadFile("agente_local.py");

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: "Outfit, sans-serif" }}>
          Agente Local
        </h1>
        <p className="text-gray-500 text-sm mt-1">Configure o acesso ao certificado A3 físico (cartão/token) ou BirdID Desktop</p>
      </div>

      {/* What is it */}
      <div className="bg-[#163A24] text-white rounded-xl p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <Cpu size={20} className="text-[#8FBC8F]" />
          </div>
          <div>
            <h2 className="font-bold mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>O que é o Agente Local?</h2>
            <p className="text-sm text-white/80">
              O Agente Local é um pequeno servidor que roda no seu computador e permite que o
              Assinador Digital acesse seu certificado A3 ou BirdID Desktop. A chave privada{" "}
              <strong className="text-white">nunca sai do hardware/nuvem do BirdID</strong> — apenas o hash
              é enviado para assinatura, garantindo máxima segurança.
            </p>
          </div>
        </div>
      </div>

      {/* Health check */}
      <div data-testid="agent-health-check" className="bg-white rounded-xl border border-black/5 shadow-sm p-5 mb-5">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            healthState === "online" ? "bg-green-50" : healthState === "offline" ? "bg-red-50" : "bg-gray-100"
          }`}>
            {healthState === "checking" ? <Loader2 size={18} className="text-gray-500 animate-spin" /> :
             healthState === "online" ? <Wifi size={18} className="text-green-600" /> :
             healthState === "offline" ? <WifiOff size={18} className="text-red-500" /> :
             <Wifi size={18} className="text-gray-400" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-gray-800">Status do Agente em localhost:7979</p>
            {healthState === "idle" && <p className="text-xs text-gray-500 mt-0.5">Clique para verificar se o agente está em execução</p>}
            {healthState === "checking" && <p className="text-xs text-gray-500 mt-0.5">Verificando conexão...</p>}
            {healthState === "online" && healthData && (
              <p className="text-xs text-green-700 mt-0.5" data-testid="health-online-msg">
                ✓ Agente <strong>{healthData.type || "online"}</strong> v{healthData.version || "?"} ·
                {healthData.cert_count ?? 0} certificado(s)
                {healthData.birdid_detected ? " · BirdID detectado" : ""}
              </p>
            )}
            {healthState === "offline" && (
              <p className="text-xs text-red-600 mt-0.5" data-testid="health-offline-msg">
                ✗ Agente não está rodando. Inicie pelo iniciar_agente.bat ou python agente_local.py
              </p>
            )}
          </div>
          <button
            data-testid="btn-check-agent-health"
            onClick={checkHealth}
            disabled={healthState === "checking"}
            className="flex items-center gap-1.5 bg-[#163A24] text-white px-4 py-2 rounded-md text-xs font-semibold hover:bg-[#205234] disabled:opacity-50 transition-colors flex-shrink-0"
          >
            {healthState === "checking" ? <><Loader2 size={12} className="animate-spin" /> Verificando</> : <>Testar conexão</>}
          </button>
        </div>
      </div>

      {/* Zero-install (PowerShell) — Recomendado */}
      <div
        data-testid="zero-install-section"
        className="bg-white rounded-xl border-2 border-[#163A24] shadow-sm p-6 mb-6"
      >
        <div className="flex items-center gap-2 mb-2">
          <Zap size={18} className="text-[#163A24]" />
          <h2 className="font-bold text-[#1A1A1A] text-base" style={{ fontFamily: "Outfit, sans-serif" }}>
            Modo Zero-Install (Recomendado para Windows)
          </h2>
          <span className="ml-auto text-[10px] uppercase tracking-wider bg-[#163A24] text-white px-2 py-1 rounded-full font-bold">
            Sem Python
          </span>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Use o script PowerShell que vem nativo no Windows 10/11. Não precisa instalar Python,
          dependências ou drivers extras — basta baixar os 2 arquivos abaixo, colocar na mesma pasta e dar duplo-clique no <code className="bg-gray-100 px-1 rounded text-xs">iniciar_agente.bat</code>.
        </p>

        <div className="space-y-3">
          {ZERO_INSTALL_FILES.map(({ filename, label, desc, icon: Icon }) => (
            <div
              key={filename}
              data-testid={`zero-install-${filename}`}
              className="flex items-start gap-3 p-3 border border-gray-100 rounded-lg hover:bg-[#FAF9F6] transition-colors"
            >
              <div className="w-9 h-9 rounded-md bg-[#163A24]/10 flex items-center justify-center flex-shrink-0">
                <Icon size={16} className="text-[#163A24]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-gray-800 truncate">{label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                <code className="text-[11px] text-gray-400 font-mono">{filename}</code>
              </div>
              <button
                data-testid={`btn-download-${filename}`}
                onClick={() => downloadFile(filename)}
                className="flex items-center gap-1.5 bg-[#163A24] text-white px-3 py-2 rounded-md text-xs font-semibold hover:bg-[#205234] transition-colors flex-shrink-0"
              >
                <Download size={12} /> Baixar
              </button>
            </div>
          ))}
        </div>

        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700 flex items-start gap-2">
          <CheckCircle size={14} className="mt-0.5 flex-shrink-0" />
          <div>
            <strong>Como usar:</strong> Baixe <code className="bg-white/60 px-1 rounded">agente_assinador_v14.ps1</code> e <code className="bg-white/60 px-1 rounded">iniciar_agente.bat</code>,
            coloque os dois na mesma pasta e dê duplo-clique no <code className="bg-white/60 px-1 rounded">.bat</code>. O agente abre na porta 7979.
          </div>
        </div>
      </div>

      {/* Python install (alternative) */}
      <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6 mb-5">
        <h2 className="font-bold text-[#1A1A1A] mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          Alternativa: Agente em Python
        </h2>
        <p className="text-xs text-gray-500 mb-5">Multi-plataforma (Windows / Linux / macOS). Requer Python 3.8+.</p>
        <div className="space-y-5">
          {PYTHON_INSTALL_STEPS.map((step, i) => (
            <div key={i} className="flex items-start gap-4">
              <div className="w-7 h-7 bg-[#163A24] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                {i + 1}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-800 text-sm mb-2">{step.title}</p>
                {step.desc && <p className="text-xs text-gray-500 mb-2">{step.desc}</p>}
                {step.action === "download" ? (
                  <button
                    data-testid="btn-download-agent"
                    onClick={handleDownloadPython}
                    className="flex items-center gap-2 bg-[#163A24] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#205234] transition-colors"
                  >
                    <Download size={14} /> Baixar agente_local.py
                  </button>
                ) : step.code ? (
                  <div className="code-block p-3 rounded-md">
                    <code className="text-xs text-green-400">{step.code}</code>
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Platforms */}
      <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6 mb-5">
        <h2 className="font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
          Compatibilidade de Plataformas
        </h2>
        <div className="grid grid-cols-3 gap-4 text-center text-sm mb-5">
          {[
            { icon: Monitor, label: "Windows", desc: "10, 11 (PowerShell + Python)" },
            { icon: Server, label: "Linux", desc: "Ubuntu, Debian (Python)" },
            { icon: Apple, label: "macOS", desc: "10.14+ (Python)" },
          ].map(({ icon: Icon, label, desc }) => (
            <div key={label} className="p-4 bg-[#FAF9F6] rounded-lg border border-gray-100">
              <Icon size={20} className="text-[#163A24] mx-auto mb-2" />
              <p className="font-semibold text-gray-700">{label}</p>
              <p className="text-xs text-gray-400">{desc}</p>
              <CheckCircle size={14} className="text-green-500 mx-auto mt-2" />
            </div>
          ))}
        </div>

        <h3 className="font-semibold text-sm text-gray-700 mb-3">Drivers PKCS#11 Suportados</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead>
              <tr className="bg-gray-50">
                <th className="p-2 font-semibold text-gray-500 uppercase tracking-wider">Token/Cartão</th>
                <th className="p-2 font-semibold text-gray-500 uppercase tracking-wider">Windows</th>
                <th className="p-2 font-semibold text-gray-500 uppercase tracking-wider">Linux/Mac</th>
              </tr>
            </thead>
            <tbody>
              {DRIVERS.map((d) => (
                <tr key={d.name} className="border-b border-gray-50 last:border-0">
                  <td className="p-2 font-medium text-gray-700">{d.name}</td>
                  <td className="p-2 font-mono text-gray-500 text-[11px]">{d.windows}</td>
                  <td className="p-2 font-mono text-gray-500 text-[11px]">{d.linux}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* BirdID Desktop Section */}
      <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6 mb-5">
        <div className="flex items-center gap-2 mb-4">
          <Smartphone size={18} className="text-[#163A24]" />
          <h2 className="font-bold text-[#1A1A1A] text-base" style={{ fontFamily: "Outfit, sans-serif" }}>BirdID Desktop (Certificado em Nuvem)</h2>
        </div>
        <div className="bg-[#163A24] text-white rounded-xl p-5 mb-4">
          <div className="flex items-start gap-3">
            <ShieldCheck size={20} className="text-[#8FBC8F] flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-sm mb-1">Como funciona o BirdID com o Agente</p>
              <p className="text-xs text-white/80">
                O BirdID Desktop instala o driver PKCS#11 virtual (<code className="bg-white/10 px-1 rounded">cess_pkcs11_64.dll</code>) e
                importa o certificado para o Windows Certificate Store. O agente PowerShell usa o Windows CNG/CAPI nativo para assinar.
                O "PIN" é o código OTP de 6 dígitos do app BirdID — a chave privada fica na nuvem da Soluti/VaultID.
              </p>
            </div>
          </div>
        </div>
        <div className="space-y-3">
          {[
            { step: "1", text: "Baixe e instale o BirdID Desktop em vaultid.com.br/birdid-desktop" },
            { step: "2", text: "Baixe os arquivos zero-install acima e dê duplo-clique em iniciar_agente.bat" },
            { step: "3", text: "O agente PowerShell detecta automaticamente o certificado BirdID importado" },
            { step: "4", text: "Na assinatura, selecione 'BirdID Desktop', abra o app no celular e insira o OTP" },
          ].map(({ step, text }) => (
            <div key={step} className="flex items-start gap-3">
              <span className="w-6 h-6 bg-[#163A24] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{step}</span>
              <p className="text-sm text-gray-600 mt-0.5">{text}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700 flex items-start gap-2">
          <CheckCircle size={14} className="mt-0.5 flex-shrink-0" />
          <span><strong>Sem custo adicional!</strong> O BirdID Desktop é gratuito e usa o certificado que você já possui. Não requer API paga.</span>
        </div>
      </div>

      {/* HTTPS / Localhost */}
      <div data-testid="https-section" className="bg-white rounded-xl border border-black/5 shadow-sm p-6 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Lock size={18} className="text-[#163A24]" />
          <h2 className="font-bold text-[#1A1A1A] text-base" style={{ fontFamily: "Outfit, sans-serif" }}>
            HTTPS no Agente Local (mixed-content)
          </h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Se o sistema do prontuário rodar em <code className="bg-gray-100 px-1 rounded text-xs">https://</code>, o navegador
          bloqueia chamadas para <code className="bg-gray-100 px-1 rounded text-xs">http://localhost:7979</code> (mixed-content).
          Use uma das soluções abaixo:
        </p>

        <div className="space-y-3">
          <div className="border border-gray-100 rounded-lg p-4 bg-[#FAF9F6]">
            <p className="font-semibold text-sm text-gray-800 mb-1">Opção 1 — mkcert (recomendado, certificado válido localmente)</p>
            <p className="text-xs text-gray-500 mb-2">
              Gera um certificado SSL confiável para localhost. Funciona sem aviso do navegador.
            </p>
            <pre className="code-block p-3 rounded-md text-xs"><code className="text-green-400">{`# Instalar mkcert (Windows com Chocolatey ou PowerShell)
choco install mkcert
mkcert -install
mkcert localhost 127.0.0.1 ::1
# gera localhost+2.pem e localhost+2-key.pem na pasta atual

# Inicie o agente python com SSL:
python agente_local.py --cert localhost+2.pem --key localhost+2-key.pem`}</code></pre>
          </div>

          <div className="border border-gray-100 rounded-lg p-4">
            <p className="font-semibold text-sm text-gray-800 mb-1">Opção 2 — Subdomínio público apontando para 127.0.0.1</p>
            <p className="text-xs text-gray-500 mb-2">
              Cadastre <code className="bg-gray-100 px-1 rounded text-xs">agente.seudominio.com.br</code> no DNS público
              apontando para <code className="bg-gray-100 px-1 rounded text-xs">127.0.0.1</code> e instale um certificado
              real (Let's Encrypt) — assim o navegador trata como HTTPS legítimo.
            </p>
          </div>

          <div className="border border-gray-100 rounded-lg p-4">
            <p className="font-semibold text-sm text-gray-800 mb-1">Opção 3 — Servir o sistema também em HTTP</p>
            <p className="text-xs text-gray-500">
              Solução mais simples mas menos segura. Acesse o prontuário via <code className="bg-gray-100 px-1 rounded text-xs">http://</code> em
              ambientes internos confiáveis.
            </p>
          </div>
        </div>
      </div>

      {/* Notice */}
      <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-xl mb-5">
        <AlertTriangle size={18} className="text-yellow-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold text-yellow-800">HTTPS e Acesso Localhost</p>
          <p className="text-yellow-700 mt-1">
            Navegadores modernos podem bloquear requisições de páginas HTTPS para localhost (HTTP).
            Para uso em produção, acesse o sistema via HTTP local ou configure o agente com um certificado SSL.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          data-testid="btn-go-sign"
          onClick={() => navigate("/sign")}
          className="flex items-center gap-2 bg-[#163A24] text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-[#205234] transition-colors"
        >
          <Terminal size={15} /> Ir para Assinatura
        </button>
        <button
          onClick={handleDownloadPython}
          className="flex items-center gap-2 border border-gray-200 text-gray-600 px-5 py-2.5 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
          data-testid="btn-download-agent-2"
        >
          <Download size={15} /> Baixar Agente Python
        </button>
        <button
          onClick={() => downloadFile("agente_assinador_v14.ps1")}
          className="flex items-center gap-2 border border-gray-200 text-gray-600 px-5 py-2.5 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
          data-testid="btn-download-ps1"
        >
          <Download size={15} /> Baixar PowerShell v14
        </button>
      </div>
    </div>
  );
}
